import React, {
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { useTranslation } from 'react-i18next';
import { Button, Collapsible } from 'ncoded-component-library';
import BuySideInstance from './BuySideInstance';
import SuitcaseIcon from 'icons/Suitcase.icon';
import ResolvePopup from 'components/ResolvePopup';
import PlusIcon from 'icons/Plus.icon';
import ExpandIcon from 'icons/Expand.icon';
import LinkIcon from 'icons/Link.icon';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useMultiCallbackRef from 'hooks/useMultiCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useMembers from '../../../../hooks/useMembers';
import { MEMBERS_TWO_SELECTOR } from 'router/subrouters/DataRoom/providers/Onboarding/data';
import AddGroupMember from '../AddGroupMember';
import MergeBuySideGroups from '../MergeBuySideGroups';
import { getArrayLength } from 'utils';

import './BuySide.styles.scss';

type BuySideProps = {
  className?: string;
  onlyNew?: boolean;
  onClick?: () => void;
};

const BuySide: React.ForwardRefRenderFunction<CollapsibleRef, BuySideProps> = (
  props,
  ref,
) => {
  const { className, onlyNew = false, onClick } = props;

  const [buySide, buySideRef] = useCallbackRef<CollapsibleRef>(null);
  const [addMember, addMemberRef] = useCallbackRef<SideDrawerRef>(null);
  const [mergeGroups, mergeGroupsRef] = useCallbackRef<SideDrawerRef>(null);

  const [buySideGroupsCollapse, buySideGroupsCollapseRef] =
    useMultiCallbackRef<CollapsibleRef>();

  const { doesntHaveAccess } = useContext(DataRoomContext);

  const { buySideGroups, buySideMembers } = useMembers(onlyNew);

  const { t } = useTranslation();

  const classes = classNames('buy-side', 'white-panel', className);

  useImperativeHandle(ref, () => buySide, [buySide]);

  const showAddGroupButton = !onlyNew;
  const showMergeGroupsButton = !onlyNew && getArrayLength(buySideGroups) >= 2;

  const areAllCollapisblesOpen = useMemo(
    () => buySideGroupsCollapse.every((group) => group?.open),
    [buySideGroupsCollapse],
  );

  return (
    <>
      <Collapsible
        className={classes}
        defaultOpen
        ref={buySideRef}
        trigger={
          <div className="trigger" onClick={onClick}>
            <div className="trigger__title">
              <SuitcaseIcon />
              <p className="name" id={MEMBERS_TWO_SELECTOR}>
                {t('buySide')}
              </p>

              <ResolvePopup onHover popupContent={t('buySideMembersPopup')}>
                <InformationcircleIcon />
              </ResolvePopup>

              <p>
                {t('numOfMembers', { count: buySideMembers?.length })} {t('in')}{' '}
                {t('numOfGroups', { count: buySideGroups.length })}
              </p>
            </div>

            <div className="trigger__actions">
              {!doesntHaveAccess && showMergeGroupsButton && (
                <Button
                  icon={LinkIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    mergeGroups?.setOpen(true);
                  }}
                >
                  {t('mergeGroups')}
                </Button>
              )}

              {!doesntHaveAccess && showAddGroupButton && (
                <Button
                  icon={PlusIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    addMember?.setOpen(true);
                  }}
                >
                  {t('addMember')}
                </Button>
              )}

              <Button
                icon={ExpandIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick?.();
                  buySide?.setOpen(true);
                  buySideGroupsCollapse?.map((group) =>
                    group?.setOpen(!areAllCollapisblesOpen),
                  );
                }}
              >
                {t(areAllCollapisblesOpen ? 'collapseAll' : 'expandAll')}
              </Button>
            </div>
          </div>
        }
      >
        {buySideGroups?.map((group, i) => (
          <BuySideInstance
            ref={buySideGroupsCollapseRef(i)}
            key={group._id}
            group={group}
            showEditGroupButton={!onlyNew}
          />
        ))}
      </Collapsible>
      <AddGroupMember ref={addMemberRef} />
      <MergeBuySideGroups ref={mergeGroupsRef} />
    </>
  );
};

export default forwardRef(BuySide);
